/* =========================================================================
 * THE TAM BASIS — a manager sets volume and price per sport × tier × award,
 * and the number a rep will be shown moves as they type.
 *
 * 🔴 WHAT THIS SCREEN IS FOR, AND WHAT IT IS NOT. Brian: "I want to hand my
 * team a TAM that shows real opportunity." So the deliverable is not this
 * screen — it is a number fifteen reps will read and act on. Everything below
 * is built around the risk that comes with that: a TAM is a CEILING, and a big
 * number handed to a rep reads as money on the table unless something says
 * otherwise FIRST. Hence the banner above the totals, the thin-sample figure
 * beside them, and the priced-vs-unpriced share printed next to the headline
 * rather than buried in a tooltip.
 *
 * ⚠️ THE CLIENT GATE HERE IS UX ONLY. `/api/tam/basis` is `money.read` /
 * `money.write` server-side, exec-only in POLICY, with a second isExec lock in
 * each handler and the path on MONEY_SURFACES so no machine reaches it either.
 * Hiding this page from a rep is a courtesy. The refusal below renders the
 * server's own sentence VERBATIM, because "a rep cannot see a price point" and
 * "there are no rows" are different facts and must read differently.
 *
 * ⚠️ NOTHING IS RECOMPUTED HERE. The three totals, the coverage share, the
 * thin-sample exposure, the ceilings and the breach flags all arrive computed.
 * A second implementation on the client is a second answer, and the two drift.
 * ========================================================================= */
import { useCallback, useEffect, useMemo, useState } from 'react'
import PageHeader from '../components/PageHeader.jsx'
import { api } from '../lib/api.js'
import s from './TamBasis.module.css'

const PAGE = 60

/* ⚠️ HOISTED, FOR THE SAME REASON BowlEligibility.jsx HOISTS ITS OWN: a
 * className template literal containing the word "card" inside a `.map()` has
 * previously been read by check-clickable as an inert record row. One constant
 * removes the misfire without weakening the gate. */
const PANEL = `card ${s.panel}`

const usd = (n) =>
  n == null || !Number.isFinite(n)
    ? null
    : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

const pct = (n) =>
  n == null || !Number.isFinite(n) ? null : `${(n * 100).toFixed(1)}%`

const num = (n) =>
  n == null || !Number.isFinite(n) ? null : n.toLocaleString('en-US')

/* ⚠️ null is NOT zero and must never render as one. Every figure on this page
 * goes through here, so a gap reads as a dash and an actual zero could not be
 * produced by accident. */
const Fig = ({ value, format = num }) => {
  const out = format(value)
  return <b>{out == null ? '—' : out}</b>
}

function RowCard({ row, onSave, saving, error }) {
  const [vol, setVol] = useState(row.enteredVolume ?? '')
  const [price, setPrice] = useState(row.enteredPriceUsd ?? '')

  /* Re-sync when the server's copy of this row changes under us. */
  useEffect(() => { setVol(row.enteredVolume ?? '') }, [row.enteredVolume])
  useEffect(() => { setPrice(row.enteredPriceUsd ?? '') }, [row.enteredPriceUsd])

  const dirty = String(vol) !== String(row.enteredVolume ?? '')
    || String(price) !== String(row.enteredPriceUsd ?? '')

  /* Live ceiling check as they type, so the breach is visible BEFORE the save
   * rather than after. It still never clamps the value. */
  const typedOver = row.ceilingUsd != null && price !== '' && Number(price) > row.ceilingUsd

  return (
    <div className={s.row}>
      <div className={s.rowHead}>
        <div className={s.rowTitle}>
          {row.sport} <span className={s.tier}>· {row.tier}</span>
        </div>
        {row.opportunityUsd == null
          ? <div className={s.rowOppNone}>no figure yet</div>
          : <div className={s.rowOpp}>{usd(row.opportunityUsd)}</div>}
      </div>

      <div className={s.badges}>
        <span className={`${s.badge} ${s.bAward}`}>{row.award}</span>
        {row.provenance === 'MEASURED' && <span className={`${s.badge} ${s.bMeasured}`}>measured</span>}
        {row.provenance === 'ENTERED' && <span className={`${s.badge} ${s.bEntered}`}>entered</span>}
        {row.provenance === 'MODELLED' && <span className={`${s.badge} ${s.bModelled}`}>modelled</span>}
        {row.thin && <span className={`${s.badge} ${s.bThin}`}>thin n={row.n}</span>}
        {row.breachesCeiling && <span className={`${s.badge} ${s.bBreach}`}>over ceiling</span>}
      </div>

      <div className={s.facts}>
        <div className={s.fact}><span>records</span><Fig value={row.records} /></div>
        <div className={s.fact}>
          <span>measured units{row.n ? ` (n=${row.n})` : ''}</span>
          <Fig value={row.measuredUnits} />
        </div>
        <div className={s.fact}><span>units used</span><Fig value={row.units} /></div>
        <div className={s.fact}>
          <span>price · {row.priceSource}</span>
          <Fig value={row.pricePerUnitUsd} format={usd} />
        </div>
      </div>

      {/* A blank is a GAP WITH A REASON, and the reason is the point. */}
      {row.reason && <p className={s.reason}>{row.reason}</p>}
      {row.thin && !row.reason && <p className={s.reason}>{row.thinNote}</p>}
      {row.breachNote && <p className={s.breach}>{row.breachNote}</p>}

      <p className={s.ceilingLine}>
        <b>Ceiling:</b>{' '}
        {row.ceilingUsd == null
          ? row.ceilingStatus /* never a zero — an absence of a ceiling is not $0 */
          : `${usd(row.ceilingUsd)} maximum award value`}
        {' · '}<b>{row.ceilingEvidence}</b>
        {' · '}{row.ceilingCitation}
      </p>

      <div className={s.edit}>
        <div>
          <label htmlFor={`v-${row.key}`}>Volume (units)</label>
          <input
            id={`v-${row.key}`} type="number" min="1" step="1" inputMode="numeric"
            placeholder="—" value={vol}
            onChange={(e) => setVol(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor={`p-${row.key}`}>Price / unit</label>
          <input
            id={`p-${row.key}`} type="number" min="1" step="1" inputMode="decimal"
            className={typedOver ? s.over : undefined}
            placeholder={String(row.pricePerUnitUsd ?? '')} value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </div>
        <div className={s.editActions}>
          <button
            type="button"
            disabled={!dirty || saving}
            onClick={() => onSave(row, vol, price)}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {(row.enteredVolume != null || row.enteredPriceUsd != null) && (
            <button
              type="button" className={s.clear} disabled={saving}
              onClick={() => { setVol(''); setPrice(''); onSave(row, '', '') }}
            >
              Clear
            </button>
          )}
          {row.enteredBy && (
            <span className={s.editMeta}>
              entered by {row.enteredBy}
              {row.enteredAt ? ` · ${new Date(row.enteredAt).toLocaleString()}` : ''}
            </span>
          )}
        </div>
        {error && <div className={s.editErr}>{error}</div>}
      </div>
    </div>
  )
}

export default function TamBasis() {
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [tier, setTier] = useState('')
  const [award, setAward] = useState('Championship')
  const [onlyGaps, setOnlyGaps] = useState(false)
  const [limit, setLimit] = useState(PAGE)
  const [savingKey, setSavingKey] = useState(null)
  const [rowErr, setRowErr] = useState({})

  const load = useCallback(async (signal) => {
    setLoading(true)
    try {
      const res = await api.tamBasis({ signal })
      setData(res)
      setErr(null)
    } catch (e) {
      /* Render the server's sentence, not a sentence of our own. */
      setErr({
        status: e?.status ?? null,
        detail: e?.body?.detail || e?.detail || e?.message || 'The basis could not be read.',
        error: e?.body?.error || e?.error || null,
      })
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const c = new AbortController()
    load(c.signal)
    return () => c.abort()
  }, [load])

  const save = useCallback(async (row, volume, priceUsd) => {
    setSavingKey(row.key)
    setRowErr((p) => ({ ...p, [row.key]: null }))
    try {
      await api.tamBasisSet({
        sport: row.sport, tier: row.tier, award: row.award, volume, priceUsd,
      })
      await load()
    } catch (e) {
      setRowErr((p) => ({
        ...p,
        [row.key]: e?.body?.detail || e?.detail || e?.message || 'That figure was refused.',
      }))
    } finally {
      setSavingKey(null)
    }
  }, [load])

  const tiers = useMemo(
    () => [...new Set((data?.rows || []).map((r) => r.tier))].sort(),
    [data])

  const rows = useMemo(() => {
    let out = data?.rows || []
    if (award) out = out.filter((r) => r.award === award)
    if (tier) out = out.filter((r) => r.tier === tier)
    if (onlyGaps) out = out.filter((r) => r.units == null)
    const needle = q.trim().toLowerCase()
    if (needle) out = out.filter((r) => r.sport.toLowerCase().includes(needle))
    return out
  }, [data, award, tier, onlyGaps, q])

  /* ── LOADING. It says what it is reading and reserves the shape of four
   * cards, so the page does not jump when they land. It used to be one line of
   * 13px grey text on a blank page, which on a phone is indistinguishable from
   * a screen that has finished loading and found nothing. */
  if (loading) {
    return (
      <div className={s.wrap}>
        <PageHeader eyebrow="Manager only" title="TAM basis" />
        <div className={PANEL} aria-busy="true">
          <p className={s.state}>Reading the basis…</p>
          <div className={s.skeleton} aria-hidden="true"><i /><i /><i /><i /></div>
        </div>
      </div>
    )
  }

  /* A REFUSAL IS NOT AN EMPTY TABLE. */
  if (err) {
    return (
      <div className={s.wrap}>
        <PageHeader eyebrow="Manager only" title="TAM basis" />
        <div className={s.refusal}>
          <strong>
            {err.status === 403 ? 'This screen is manager-only.' : 'The basis could not be read.'}
          </strong>
          {err.detail}
          {err.status === 403 && (
            <>
              {' '}This is enforced on the server, not by hiding a control — the price per unit and
              the dollar opportunity are not sent to a browser that is not entitled to them.
            </>
          )}
        </div>
      </div>
    )
  }

  const t = data.totals
  const c = data.coverage
  const r = data.risk

  return (
    <div className={s.wrap}>
      {/* ⭐ THE PAGE HEADER THE REST OF THE APP USES, AND IT WAS THE ONE THING
          THIS SCREEN HAD NEVER HAD. It carried a bare `<h1>` at a hand-typed
          20px and a 12px grey line under it, so it was the only page in the
          product with no eyebrow, no lead and its own heading size — which is
          most of why it read as a different application. The eyebrow earns its
          place here rather than decorating: "Manager only" is the single most
          important thing to know before reading a dollar figure on this page,
          and it was previously a word in the middle of a sentence. */}
      <PageHeader
        eyebrow="Manager only"
        title="TAM basis"
        lead={`Volume and price per sport × tier × award type — the figures every rep's opportunity number is built from. ${num(data.rows.length)} rows.`}
      />

      {/* 🔴 BEFORE THE NUMBER, NOT AFTER IT. */}
      <div className={s.ceilingBanner}>
        <strong>This is a TAM — a ceiling, not a pipeline.</strong>
        <p>{data.notPipeline}</p>
      </div>

      {data.store?.degraded && (
        <div className={s.degraded}>
          <b>Entered figures are not durable right now.</b> {data.store.detail}
        </div>
      )}

      <div className={s.totals}>
        <div className={`${s.total} ${s.measured}`}>
          <div className={s.label}>Measured</div>
          <div className={s.value}>{usd(t.measuredUsd) ?? '—'}</div>
          <div className={s.note}>Units and price both from delivered orders.</div>
        </div>
        <div className={`${s.total} ${s.entered}`}>
          <div className={s.label}>Entered</div>
          <div className={s.value}>{usd(t.enteredUsd) ?? '—'}</div>
          <div className={s.note}>A figure someone typed.</div>
        </div>
        <div className={`${s.total} ${s.modelled}`}>
          <div className={s.label}>Modelled</div>
          <div className={s.value}>{usd(t.modelledUsd) ?? '—'}</div>
          <div className={s.note}>Measured units × the default price.</div>
        </div>
        <div className={`${s.total} ${s.combined}`}>
          <div className={s.label}>Combined</div>
          <div className={s.value}>{usd(t.combinedUsd) ?? '—'}</div>
          <div className={s.note}>{t.combinedNote}</div>
        </div>
      </div>

      {/* The thin-sample exposure sits HERE, beside the total, by design. */}
      <div className={s.risk}>
        <div className={s.big}>
          {pct(r.thinShare) ?? '—'} of the total rests on a thin sample
          {' '}({usd(r.thinUsd) ?? '—'} across {num(r.thinRows) ?? '—'} rows, each n&lt;5)
        </div>
        <p>{r.thinNote}</p>
        {r.largestRow && (
          <p>
            Largest single row: <b>{usd(r.largestRow.opportunityUsd)}</b> on{' '}
            <b>{r.largestRow.key.replace(/\|\|/g, ' · ')}</b> with n={r.largestRow.n}
            {r.largestRow.thin ? ' — a thin sample.' : '.'} {r.largestRowNote}
          </p>
        )}
        {r.breaches > 0 && (
          <p><b>{r.breaches}</b> row(s) carry a price above a governing-body ceiling. Flagged, not clamped.</p>
        )}
      </div>

      {/* ⚠️ THE COVERAGE BLOCK AND THE CENSUS BELOW IT ARE NOW CARDS. They were
          a bordered <dl> and a bare run of grey text, so the page went from
          two loud red panels straight into unstructured prose with no surface
          under it — which at 390px is where it stopped looking like a screen
          and started looking like a document. */}
      <dl className={`${PANEL} ${s.coverage}`}>
        <div>
          <dt>Records priced</dt>
          <dd>{num(c.pricedRecords) ?? '—'}</dd>
        </div>
        <div>
          <dt>Records unpriced</dt>
          <dd>{num(c.unpricedRecords) ?? '—'}</dd>
        </div>
        <div>
          <dt>Share of the board covered</dt>
          <dd>{pct(c.sharePriced) ?? '—'}</dd>
        </div>
        <div>
          <dt>Records in the snapshot</dt>
          <dd>{num(c.snapshotRecords) ?? '—'}</dd>
        </div>
        <div className={s.span2}>{c.note}</div>
        {c.withoutSportNote && <div className={s.span2}>{c.withoutSportNote}</div>}
      </dl>

      <div className={`${PANEL} ${s.census}`}>
        <div>
          <b>Price default:</b> {usd(data.price.defaultUsd)} / unit · {data.price.defaultSource}
          {' · shape: '}{data.price.shape}. {data.price.shapeNote}
        </div>
        <div className={s.censusWarn}>{data.recordsReadAtNote}</div>
        <div>
          Orders measured at <b>{data.measuredAt || 'unknown'}</b>; records read at{' '}
          <b>{data.recordsReadAt || 'unknown'}</b>.
        </div>
        <div>{data.byScopeNote}</div>
        <div>{data.byRepNote}</div>
      </div>

      <div className={s.controls}>
        <input
          type="search" placeholder="Filter by sport…" value={q}
          onChange={(e) => { setQ(e.target.value); setLimit(PAGE) }}
        />
        <select value={award} onChange={(e) => { setAward(e.target.value); setLimit(PAGE) }}>
          {(data.awards || []).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={tier} onChange={(e) => { setTier(e.target.value); setLimit(PAGE) }}>
          <option value="">All tiers</option>
          {tiers.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        {/* ⚠️ THE INLINE STYLE OBJECT IS GONE AND IT WAS NOT HARMLESS. It set
            `fontSize: 12` as a JS number, which is a px literal that no
            stylesheet scan can see and no token can govern — and it left the
            label at whatever height 12px of text produces, roughly 18px, on a
            control a manager taps to filter the whole board. `.gapToggle`
            carries the 44px. */}
        <label className={s.gapToggle}>
          <input
            type="checkbox" checked={onlyGaps}
            onChange={(e) => { setOnlyGaps(e.target.checked); setLimit(PAGE) }}
          />
          Only rows with no figure
        </label>
      </div>

      <p className={s.sub}>
        Showing {num(Math.min(limit, rows.length))} of {num(rows.length)} rows, highest record
        count first.
      </p>

      <div className={s.rows}>
        {rows.slice(0, limit).map((row) => (
          <RowCard
            key={row.key}
            row={row}
            onSave={save}
            saving={savingKey === row.key}
            error={rowErr[row.key]}
          />
        ))}
      </div>

      {rows.length === 0 && (
        <p className={s.state}>
          No rows match that filter. That is a filter result, not an empty board — clear the
          filters to see all {num(data.rows.length)} rows.
        </p>
      )}

      {limit < rows.length && (
        <button type="button" className={s.more} onClick={() => setLimit((n) => n + PAGE)}>
          Show {Math.min(PAGE, rows.length - limit)} more
        </button>
      )}
    </div>
  )
}
